/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    type ElementType,
    type ImgHTMLAttributes,
    type SyntheticEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { preload } from 'react-dom';
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { cn, isServer } from '@/lib/utils';
import {
    type DynamicImageDimensions,
    replaceImageFormat,
    resolveDynamicImageAttributes,
} from '@/lib/images/dynamic-image';
import { useDynamicImageContext } from '@/providers/dynamic-image';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import type { ComponentType } from '@/components/region';
import type { Image } from '@/types';
// RGBARRY-CUSTOMIZATION: shared Page Designer 'Show full image' option
import { FULL_IMAGE_CLASSES, isFullImage } from '@/lib/page-designer/style-attributes';

interface DynamicImageProps {
    // RGBARRY-CUSTOMIZATION: PD supplies image attributes as objects; retaining desktop/mobile metadata
    // reserves the selected asset's aspect ratio while supporting responsive art direction.
    src: string | Image;
    /** Optional mobile-specific image selected below the md breakpoint (768px). */
    mobileImage?: string | Image;
    // END RGBARRY-CUSTOMIZATION
    alt?: string;
    /**
     * Image widths relative to the breakpoints. Supports multiple formats:
     * - Array of numbers: [100, 360, 720] (unitless, interpreted as px)
     * - Array of strings with units: ['50vw', '100vw', '500px'] (mixed px and vw units)
     * - Object with breakpoint keys: {base: 100, sm: 360, md: 720} (unitless, interpreted as px)
     * - Object with breakpoint keys and units: {base: '100vw', sm: '50vw', md: '500px'}
     * - String (for Page Designer): comma-separated widths (e.g., "400,800,1200")
     */
    widths?: DynamicImageDimensions | string;
    /**
     * Image heights relative to the breakpoints, used for DIS server-side cropping via the `sh`
     * parameter. Supports the same formats as `widths`. When provided alongside `widths`, defines
     * the exact crop box on the DIS server. When omitted, DIS preserves the original aspect ratio.
     * - String (for Page Designer): comma-separated heights (e.g., "300,600,900")
     */
    heights?: DynamicImageDimensions | string;
    imageProps?: ImgHTMLAttributes<HTMLImageElement>;
    as?: ElementType;
    className?: string;
    loading?: HTMLImageElement['loading'];
    priority?: HTMLImageElement['fetchPriority'];
    // Page Designer styling props
    objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
    borderRadius?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full';
    boxShadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    padding?: '0' | '1' | '2' | '3' | '4' | '6' | '8';
    margin?: '0' | '1' | '2' | '3' | '4' | '6' | '8';
    hoverEffect?: 'none' | 'scale' | 'opacity' | 'shadow' | 'brightness';
    // RGBARRY-CUSTOMIZATION: Page Designer 'Show full image' (natural height, no crop). Unset = current behavior.
    imageDisplay?: string;
    // Page Designer props (need to be extracted to avoid passing to DOM)
    regionId?: string;
    component?: ComponentType;
    componentData?: Record<string, Promise<unknown>>;
    designMetadata?: ComponentDesignMetadata;
    data?: unknown;
}

/* v8 ignore start - do not test decorators in unit tests, decorator functionality is tested separately*/
@Component('pdImage', {
    name: 'Image',
    description: 'Responsive image component with Dynamic Imaging Service support and customizable styling',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class DynamicImageMetadata {
    @AttributeDefinition({
        id: 'src',
        name: 'Image URL',
        description: 'The source URL of the image',
        type: 'image',
        required: true,
    })
    src?: string;

    // RGBARRY-CUSTOMIZATION: expose the optional mobile PLP asset in Page Designer.
    @AttributeDefinition({
        id: 'mobileImage',
        name: 'Mobile Image',
        description: 'Optional image used below 768px. Falls back to Image URL when not provided.',
        type: 'image',
    })
    mobileImage?: string;
    // END RGBARRY-CUSTOMIZATION

    @AttributeDefinition({
        id: 'alt',
        name: 'Alt Text',
        description: 'Alternative text for the image (accessibility)',
        type: 'string',
    })
    alt?: string;

    @AttributeDefinition({
        id: 'widths',
        name: 'Responsive Widths',
        description: 'Comma-separated widths in pixels (e.g., "400,800,1200")',
        type: 'string',
    })
    widths?: string;

    @AttributeDefinition({
        id: 'heights',
        name: 'Responsive Heights',
        description:
            'Comma-separated heights in pixels (e.g., "300,600,900") for DIS server-side cropping alongside widths',
        type: 'string',
    })
    heights?: string;

    @AttributeDefinition({
        id: 'objectFit',
        name: 'Object Fit',
        description: 'How the image should be resized to fit its container',
        type: 'enum',
        values: ['contain', 'cover', 'fill', 'none', 'scale-down'],
        defaultValue: 'cover',
    })
    objectFit?: string;

    @AttributeDefinition({
        id: 'borderRadius',
        name: 'Border Radius',
        description: 'Corner roundness of the image',
        type: 'enum',
        values: ['none', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', 'full'],
        defaultValue: 'none',
    })
    borderRadius?: string;

    @AttributeDefinition({
        id: 'boxShadow',
        name: 'Box Shadow',
        description: 'Shadow effect for the image',
        type: 'enum',
        values: ['none', 'sm', 'md', 'lg', 'xl', '2xl'],
        defaultValue: 'none',
    })
    boxShadow?: string;

    @AttributeDefinition({
        id: 'padding',
        name: 'Padding',
        description: 'Padding around the image in Tailwind spacing units',
        type: 'enum',
        values: ['0', '1', '2', '3', '4', '6', '8'],
        defaultValue: '0',
    })
    padding?: string;

    @AttributeDefinition({
        id: 'margin',
        name: 'Margin',
        description: 'Margin around the image in Tailwind spacing units',
        type: 'enum',
        values: ['0', '1', '2', '3', '4', '6', '8'],
        defaultValue: '0',
    })
    margin?: string;

    @AttributeDefinition({
        id: 'hoverEffect',
        name: 'Hover Effect',
        description: 'Interactive hover effect for the image',
        type: 'enum',
        values: ['none', 'scale', 'opacity', 'shadow', 'brightness'],
        defaultValue: 'none',
    })
    hoverEffect?: string;

    @AttributeDefinition({
        id: 'priority',
        name: 'Loading Priority',
        description: 'Priority hint for image loading',
        type: 'enum',
        values: ['high', 'low', 'auto'],
        defaultValue: 'auto',
    })
    priority?: string;

    // RGBARRY-CUSTOMIZATION: Page Designer 'Show full image' option
    @AttributeDefinition({
        id: 'imageDisplay',
        name: 'Style – Image Display',
        type: 'enum',
        values: ['Crop to fit', 'Show full image'],
        defaultValue: 'Crop to fit',
        description: 'Show full image keeps the whole image at its natural height (no top/bottom crop).',
    })
    imageDisplay?: string;
    // END RGBARRY-CUSTOMIZATION
}
/* v8 ignore stop */

// Helper function to map attribute values to Tailwind classes
const getImageStyleClasses = ({
    borderRadius,
    boxShadow,
    padding,
    margin,
    hoverEffect,
}: Partial<DynamicImageProps>) => {
    const classes: string[] = [];

    if (borderRadius && borderRadius !== 'none') {
        const radiusMap: Record<string, string> = {
            xs: 'rounded-xs',
            sm: 'rounded-sm',
            md: 'rounded-md',
            lg: 'rounded-lg',
            xl: 'rounded-xl',
            '2xl': 'rounded-2xl',
            '3xl': 'rounded-3xl',
            '4xl': 'rounded-4xl',
            full: 'rounded-full',
        };
        classes.push(radiusMap[borderRadius] || 'rounded-none');
        classes.push('overflow-hidden');
    }

    // Box shadow
    if (boxShadow && boxShadow !== 'none') {
        const shadowMap = {
            sm: 'shadow-sm',
            md: 'shadow-md',
            lg: 'shadow-lg',
            xl: 'shadow-xl',
            '2xl': 'shadow-2xl',
        };
        classes.push(shadowMap[boxShadow]);
    }

    // Padding
    if (padding && padding !== '0') {
        classes.push(`p-${padding}`);
    }

    // Margin
    if (margin && margin !== '0') {
        classes.push(`m-${margin}`);
    }

    // Hover effects
    if (hoverEffect && hoverEffect !== 'none') {
        const hoverMap = {
            scale: 'hover:scale-105 transition-transform',
            opacity: 'hover:opacity-90 transition-opacity',
            shadow: 'hover:shadow-lg transition-shadow',
            brightness: 'hover:brightness-110 transition-all',
        };
        classes.push(hoverMap[hoverEffect]);
    }

    return classes.join(' ');
};

// Helper function to get object-fit class for the img element
const getObjectFitClass = (objectFit?: string) => {
    if (!objectFit || objectFit === 'cover') return 'object-cover';

    const objectFitMap: Record<string, string> = {
        contain: 'object-contain',
        fill: 'object-fill',
        none: 'object-none',
        'scale-down': 'object-scale-down',
    };
    return objectFitMap[objectFit] || 'object-cover';
};

// Helper function to parse a comma-separated dimension string to an array for Page Designer
const parseDimensionsString = (value?: string | DynamicImageDimensions): DynamicImageDimensions | undefined => {
    if (!value || typeof value !== 'string') {
        return value as DynamicImageDimensions | undefined;
    }
    if (value.trim() === '') {
        return undefined;
    }

    const parsed = value
        .split(',')
        .map((v) => parseInt(v.trim(), 10))
        .filter((v) => !isNaN(v));
    return parsed.length > 0 ? parsed : undefined;
};

// RGBARRY-CUSTOMIZATION: Page Designer PLP banners can provide separate mobile art direction.
// Native picture media queries let the browser select one asset during HTML parsing without a
// client-side viewport check, hydration mismatch, or duplicate desktop/mobile image downloads.
const MOBILE_IMAGE_MEDIA = '(max-width: 767px)';
const DESKTOP_IMAGE_MEDIA = '(min-width: 768px)';

const combineMediaQueries = (media: string | undefined, viewportMedia: string): string =>
    media ? `${media} and ${viewportMedia}` : viewportMedia;

const resolvePageDesignerImageSrc = (image: unknown): string =>
    image && typeof image === 'object'
        ? ((image as Record<string, unknown>).absURL as string) ||
          ((image as Record<string, unknown>).url as string) ||
          ((image as Record<string, unknown>).disBaseLink as string) ||
          ((image as Record<string, unknown>).link as string) ||
          ''
        : ((image as string | undefined) ?? '');

const getIntrinsicDimensions = (image: unknown): { width?: number; height?: number } => {
    const metaData =
        image && typeof image === 'object'
            ? (image as { metaData?: { width?: number | string; height?: number | string } }).metaData
            : undefined;
    const toDimension = (value: number | string | undefined): number | undefined => {
        const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
        return typeof parsed === 'number' && Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
    };

    return { width: toDimension(metaData?.width), height: toDimension(metaData?.height) };
};
// END RGBARRY-CUSTOMIZATION

/**
 * Responsive image component optimized to work with the Dynamic Imaging Service.
 * Via this component it's easy to create a `<picture>` element with related
 * theme-aware `<source>` elements and responsive preloading for high-priority
 * images using React 19's `preload` function.
 * @example Widths without a unit defined as array (interpreted as px values)
 * <DynamicImage
 *   src="http://example.com/image.jpg[?sw={width}&q=60]"
 *   widths={[100, 360, 720]} />
 * @example Widths without a unit defined as object (interpreted as px values)
 * <DynamicImage
 *   src="http://example.com/image.jpg[?sw={width}&q=60]"
 *   widths={{base: 100, sm: 360, md: 720}} />
 * @example Widths with mixed px and vw units defined as array
 * <DynamicImage
 *   src="http://example.com/image.jpg[?sw={width}&q=60]"
 *   widths={['50vw', '100vw', '500px']} />
 * @example Eagerly load image with high priority and responsive preloading
 * <DynamicImage
 *   src="http://example.com/image.jpg[?sw={width}&q=60]"
 *   widths={['50vw', '50vw', '20vw', '20vw', '25vw']}
 *   imageProps={{loading: 'eager'}}
 *   />
 * @example Preload all picture sources using React 19's preload function
 * <DynamicImage
 *   src="http://example.com/image.jpg[?sw={width}&q=60]"
 *   widths={[400, 800, 1200]}
 *   priority="high"
 *   />
 * @see {@link https://web.dev/learn/design/responsive-images}
 * @see {@link https://web.dev/learn/design/picture-element}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/picture}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Guides/Responsive_images}
 * @see {@link https://help.salesforce.com/s/articleView?id=cc.b2c_image_transformation_service.htm&type=5}
 * @see {@link https://react.dev/reference/react-dom/preload}
 */
const DynamicImage = ({
    src,
    // RGBARRY-CUSTOMIZATION: consume the Page Designer mobile asset instead of forwarding it to the wrapper DOM.
    mobileImage,
    // END RGBARRY-CUSTOMIZATION
    alt = '',
    widths,
    heights,
    imageProps = {},
    as: ImageComponent = 'img',
    className,
    loading,
    priority,
    objectFit = 'cover',
    borderRadius = 'none',
    boxShadow = 'none',
    padding = '0',
    margin = '0',
    hoverEffect = 'none',
    imageDisplay, // RGBARRY-CUSTOMIZATION
    regionId: _regionId,
    component: _component,
    componentData: _componentData,
    designMetadata: _designMetadata,
    data: _data,
    ...rest
}: DynamicImageProps) => {
    const config = useConfig();

    // RGBARRY-CUSTOMIZATION: normalize both Page Designer image uploads for responsive art direction.
    // Page Designer passes image-type attributes as objects, whose URL field varies by source and context.
    const resolvedSrc = resolvePageDesignerImageSrc(src);
    const resolvedMobileSrc = resolvePageDesignerImageSrc(mobileImage);
    // END RGBARRY-CUSTOMIZATION

    // RGBARRY-CUSTOMIZATION: reserve the intrinsic aspect box to eliminate CLS. SFCC image objects
    // carry their real pixel dimensions in `metaData` (width/height); forwarding them as the <img>
    // `width`/`height` attributes lets the browser reserve the correct responsive box *before* the
    // pixels decode (Tailwind Preflight's `img { height: auto }` keeps it fluid, so the attributes act
    // as an aspect-ratio hint, not a fixed size). Without this, a full-width `pdImage` banner — e.g. the
    // PLP top banner above the product grid — renders at 0 height on first paint and shoves everything
    // below it down when the (often lazy) image finally loads. A caller-supplied width/height on
    // `imageProps` always wins; images with explicit CSS width+height (e.g. `w-full h-full`) are
    // unaffected since CSS box sizing overrides the attribute hint.
    const { width: intrinsicWidth, height: intrinsicHeight } = getIntrinsicDimensions(src);
    const { width: mobileIntrinsicWidth, height: mobileIntrinsicHeight } = getIntrinsicDimensions(mobileImage);

    // Parse widths/heights if they're strings (from Page Designer)
    const parsedWidths = useMemo(() => parseDimensionsString(widths), [widths]);
    // RGBARRY-CUSTOMIZATION: 'Show full image' ignores authored heights so DIS never crops the image.
    const fullImage = isFullImage(imageDisplay);
    const parsedHeights = useMemo(() => (fullImage ? undefined : parseDimensionsString(heights)), [heights, fullImage]);

    // Funnel through the shared resolver so the render path and the prefetch path always agree on URL math, formats,
    // and DIS state.
    const {
        sources,
        links,
        src: responsiveSrc,
        transformedSrc,
        enableDis,
        fallbackFormat,
    } = useMemo(
        () => resolveDynamicImageAttributes({ src: resolvedSrc, config, widths: parsedWidths, heights: parsedHeights }),
        [resolvedSrc, config, parsedWidths, parsedHeights]
    );
    // RGBARRY-CUSTOMIZATION: give the mobile upload the same DIS transformations as the desktop image.
    const mobileImageAttributes = useMemo(
        () =>
            resolvedMobileSrc
                ? resolveDynamicImageAttributes({
                      src: resolvedMobileSrc,
                      config,
                      widths: parsedWidths,
                      heights: parsedHeights,
                  })
                : undefined,
        [resolvedMobileSrc, config, parsedWidths, parsedHeights]
    );
    const mobileFallbackSrc = mobileImageAttributes
        ? mobileImageAttributes.enableDis
            ? replaceImageFormat(mobileImageAttributes.src, mobileImageAttributes.fallbackFormat, undefined, config)
            : mobileImageAttributes.src
        : undefined;
    // END RGBARRY-CUSTOMIZATION
    const imageContext = useDynamicImageContext();

    const effectivePriority = priority ?? (imageContext?.hasSource(transformedSrc) ? 'high' : 'auto');
    const effectiveLoading = loading ?? (effectivePriority === 'high' ? 'eager' : 'lazy');

    // RGBARRY-CUSTOMIZATION: subtle load-in fade so images resolve gently against their reserved
    // placeholder box instead of hard-popping on decode. This is a client-only enhancement — full
    // opacity is emitted on the server and on the first client render (no hydration mismatch, works
    // with no-JS), and priority/eager images are skipped entirely so LCP is never delayed.
    const canFade = effectivePriority !== 'high';
    const imgRef = useRef<HTMLImageElement>(null);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [fadeActive, setFadeActive] = useState(false);
    useEffect(() => {
        if (!canFade) return;
        const img = imgRef.current;
        if (!img) return;
        // Already decoded (cached, or painted before hydration) → keep full opacity, no fade,
        // so an already-visible image never flashes back to transparent.
        if (img.complete) {
            setImgLoaded(true);
            return;
        }
        // Not yet decoded → enable the fade; `onLoad` completes it. No pixels exist yet, so
        // switching to opacity-0 here cannot flash any visible content.
        setFadeActive(true);
    }, [canFade]);
    const fadeClasses = canFade
        ? cn('transition-opacity duration-300 motion-reduce:transition-none', fadeActive && !imgLoaded && 'opacity-0')
        : undefined;

    // Apply object-fit to imageProps
    const objectFitClass = getObjectFitClass(objectFit);
    const effectiveImageProps = {
        ...imageProps,
        ref: imgRef,
        // RGBARRY-CUSTOMIZATION: 'Show full image' wins over object-fit and fixed heights.
        className: cn(objectFitClass, imageProps.className, fullImage && FULL_IMAGE_CLASSES, fadeClasses),
        loading: effectiveLoading,
        fetchPriority: effectivePriority,
        alt,
        // RGBARRY-CUSTOMIZATION: forward intrinsic pixel dimensions so the browser reserves the aspect
        // box before decode (CLS fix — see the metaData extraction above). A caller-supplied value wins.
        width: imageProps.width ?? intrinsicWidth,
        height: imageProps.height ?? intrinsicHeight,
        src: enableDis ? replaceImageFormat(responsiveSrc, fallbackFormat, undefined, config) : responsiveSrc,
        onLoad: (event: SyntheticEvent<HTMLImageElement>) => {
            imageProps.onLoad?.(event);
            if (canFade) setImgLoaded(true);
        },
    };

    // Get styling classes from Page Designer props
    const styleClasses = getImageStyleClasses({
        borderRadius,
        boxShadow,
        padding,
        margin,
        hoverEffect,
    });

    // RGBARRY-CUSTOMIZATION: preload only the art-directed asset selected for each viewport.
    if (isServer() && effectivePriority === 'high') {
        const preloadLinks = (
            imageLinks: typeof links,
            viewportMedia?: typeof MOBILE_IMAGE_MEDIA | typeof DESKTOP_IMAGE_MEDIA
        ) => {
            imageLinks.forEach(({ type, media, sizes, srcSet, href }) => {
                preload(href, {
                    as: 'image',
                    fetchPriority: 'high',
                    imageSrcSet: srcSet,
                    imageSizes: sizes,
                    type,
                    media: viewportMedia ? combineMediaQueries(media, viewportMedia) : media,
                });
            });
        };

        if (mobileImageAttributes) {
            if (mobileImageAttributes.links.length > 0) {
                preloadLinks(mobileImageAttributes.links, MOBILE_IMAGE_MEDIA);
            } else if (mobileFallbackSrc) {
                preload(mobileFallbackSrc, { as: 'image', fetchPriority: 'high', media: MOBILE_IMAGE_MEDIA });
            }

            if (links.length > 0) {
                preloadLinks(links, DESKTOP_IMAGE_MEDIA);
            } else if (effectiveImageProps.src) {
                preload(effectiveImageProps.src, {
                    as: 'image',
                    fetchPriority: 'high',
                    media: DESKTOP_IMAGE_MEDIA,
                });
            }
        } else if (links.length > 0) {
            preloadLinks(links);
        } else if (effectiveImageProps.src) {
            // No per-breakpoint links — the image has a single static variant (no widths/heights requested, a local
            // bundled asset, or any image when DIS is disabled). Preload the one URL the <img> resolves to so an LCP
            // hero still gets its <link rel="preload">, whether or not dimensions were supplied.
            preload(effectiveImageProps.src, { as: 'image', fetchPriority: 'high' });
        }
    }
    // END RGBARRY-CUSTOMIZATION

    return (
        <>
            <div className={cn(styleClasses, className)} {...rest}>
                {/* RGBARRY-CUSTOMIZATION: native picture media selection swaps PLP art without hydration JS. */}
                {sources.length > 0 || mobileImageAttributes ? (
                    <picture>
                        {mobileImageAttributes?.sources.map(({ type, srcSet, sizes, media }, idx) => (
                            <source
                                // oxlint-disable-next-line react/no-array-index-key
                                key={`mobile-${idx}`}
                                type={type}
                                media={combineMediaQueries(media, MOBILE_IMAGE_MEDIA)}
                                sizes={sizes}
                                srcSet={srcSet}
                                width={mobileIntrinsicWidth}
                                height={mobileIntrinsicHeight}
                            />
                        ))}
                        {mobileFallbackSrc && (
                            <source
                                media={MOBILE_IMAGE_MEDIA}
                                srcSet={mobileFallbackSrc}
                                width={mobileIntrinsicWidth}
                                height={mobileIntrinsicHeight}
                            />
                        )}
                        {sources.map(({ type, srcSet, sizes, media }, idx) => (
                            <source
                                // oxlint-disable-next-line react/no-array-index-key
                                key={`desktop-${idx}`}
                                type={type}
                                media={mobileImageAttributes ? combineMediaQueries(media, DESKTOP_IMAGE_MEDIA) : media}
                                sizes={sizes}
                                srcSet={srcSet}
                            />
                        ))}
                        <ImageComponent {...effectiveImageProps} />
                    </picture>
                ) : (
                    <ImageComponent {...effectiveImageProps} />
                )}
                {/* END RGBARRY-CUSTOMIZATION */}
            </div>
        </>
    );
};

DynamicImage.displayName = 'DynamicImage';

export { DynamicImage };
export default DynamicImage;
